<template>
  <small-modal
    :title="modalTitle"
    class="add-locales-to-videos-modal"
    :fit-content="true"
    @close="close"
  >
    <div class="add-title-modal-container">
      <banner-modal
        class="banner-modal"
        :title="$t('create.step2.addLocalesToVideosModal.bannerTitle')"
        :text="$t('create.step2.addLocalesToVideosModal.bannerText')"
      />

      <div v-if="selectedCode" class="selected-locale-block">
        <button class="locale-btn">
          <img :src="currentLocale.flag" alt="" class="flag-icon" />
          <span class="locale-name">{{ getLocaleLabel(selectedCode) }}</span>
          <button class="filter-remove-icon" @click.stop="clearLocale">
            <SquaredX />
          </button>
        </button>
      </div>

      <button class="add-locale-button" @click="toggleDropdown">
        {{ $t("create.step2.addLocalesToVideosModal.button") }}
      </button>
    </div>

    <div class="buttons">
      <input
        class="button-primary --grey"
        type="button"
        :value="$t('deleteCampaign.cancel')"
        @click.prevent="close"
      />
      <label class="button-primary button-flex">
        <input
          class="submit-button"
          type="submit"
          :value="$t('create.bulk.modalButtons.confirm')"
          @click.prevent="addLocalesToVideos"
        />
      </label>
    </div>

    <div
      v-if="showDropdown"
      v-click-outside="closeLocaleDropdown"
      class="locale-dropdown"
    >
      <div
        v-for="locale in availableLocales"
        :key="locale.code"
        class="locale-item"
        @click="addLocale(locale)"
      >
        <img :src="locale.flag" alt="" class="flag-icon" />
        <span class="locale-name">{{
          locale.name[currentLang] || locale.name.en
        }}</span>
      </div>
    </div>
  </small-modal>
</template>

<script>
import SmallModal from "../shared/SmallModal";
import BannerModal from "../shared/BannerModal.vue";
import SquaredX from "@/assets/svg/squared-x-icon.svg?inline";
import { locales } from "@/utils/locales";
import { mapState } from "vuex";

export default {
  components: { SmallModal, BannerModal, SquaredX },
  props: {
    selectedVideos: { type: Array, default: () => [] },
    videoLibrary: { type: Array, default: () => [] },
    localesFromVideo: { type: Boolean, default: false },
    videoSelectedForLocales: { type: Object, default: null },
  },
  data() {
    return {
      showDropdown: false,
      selectedCode: null,
    };
  },
  computed: {
    ...mapState(["site"]),
    modalTitle() {
      const base = this.$t("create.step2.addLocalesToVideosModal.title1");
      if (this.selectedVideos.length > 1) {
        return (
          base +
          this.selectedVideos.length +
          this.$t("create.step2.addLocalesToVideosModal.title2")
        );
      }
      return (
        base +
        this.selectedVideos.length +
        this.$t("create.step2.addLocalesToVideosModal.title3")
      );
    },
    availableLocales() {
      const langObj = this.site?.site?.languageSettings || {};
      const codesInSite = Object.keys(langObj);
      return codesInSite
        .map((code) => locales.find((l) => l.code === `/${code}/`))
        .filter(Boolean);
    },
    currentLocale() {
      if (!this.selectedCode) return {};
      return (
        locales.find((l) => l.code.replace(/\//g, "") === this.selectedCode) ||
        {}
      );
    },
    selectedSetting() {
      if (!this.selectedCode) return null;
      return this.site?.site?.languageSettings?.[this.selectedCode] || null;
    },
    isArraySetting() {
      return (
        this.selectedSetting && Array.isArray(this.selectedSetting.filters)
      );
    },
    currentLang() {
      return this.$i18n.locale.split("-")[0];
    },
  },
  created() {
    this.prefillSelectedCode();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    closeLocaleDropdown() {
      this.showDropdown = false;
    },
    addLocale(locale) {
      const codeKey = locale.code.replace(/\//g, "");
      this.selectedCode = codeKey;
      this.showDropdown = false;

      this.$emit("selected-language", codeKey);
    },
    clearLocale() {
      this.selectedCode = null;
    },
    async addLocalesToVideos() {
      if (!this.selectedCode) {
        for (const id of this.selectedVideos) {
          await this.$store.dispatch("video/updateVideoLanguage", {
            videoId: id,
            language: null,
          });
        }
        this.close();
        return;
      }

      for (const id of this.selectedVideos) {
        await this.$store.dispatch("video/updateVideoLanguage", {
          videoId: id,
          language: this.selectedCode,
        });
      }

      this.close();
    },
    prefillSelectedCode() {
      if (this.localesFromVideo && this.videoSelectedForLocales) {
        this.selectedCode = this.videoSelectedForLocales.language || null;
        return;
      }
      const vids = this.videoLibrary.filter((v) =>
        this.selectedVideos.includes(v._id)
      );

      if (!vids.length) {
        this.selectedCode = null;
        return;
      }

      const langs = vids.map((v) => v.language).filter(Boolean);
      const allHave = langs.length === vids.length;
      const allSame = allHave && langs.every((l) => l === langs[0]);

      this.selectedCode = allSame ? langs[0] : null;
    },
    getLocaleLabel(codeKey) {
      const locale =
        locales.find((l) => l.code.replace(/\//g, "") === codeKey) || {};
      return (locale.name && locale.name[this.currentLang]) || locale.name.en;
    },
  },
};
</script>

<style lang="scss">
.add-locales-to-videos-modal .small-modal.fit-modal {
  width: 520px;
}
</style>

<style lang="scss" scoped>
.banner-modal {
  margin-bottom: 24px;
  min-width: 480px;
}
.selected-locale-block {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.locale-btn {
  @include base-font;
  @include font-normal;
  font-weight: 500;
  padding: 8px 16px;
  border: none;
  background-color: rgba(234, 234, 234, 1);
  border-radius: 2px;
  cursor: pointer;
  position: relative;

  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    .filter-remove-icon {
      display: flex;
      background-color: rgba(224, 224, 224, 1);
    }
  }
}

.filter-readonly-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-readonly {
  @include base-font;
  @include font-normal;
  font-weight: 500;
  padding: 8px 16px;
  border: none;
  background-color: rgba(234, 234, 234, 1);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
}

.bold {
  font-weight: 600;
}

.flag-icon {
  width: 16px;
  height: 16px;
  object-fit: cover;
  margin-right: 6px;
}
.locale-name {
  font-size: 14px;
}

.filter-remove-icon {
  position: absolute;
  right: 0%;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(234, 234, 234, 1);
  cursor: pointer;
  display: none;
  align-items: center;
  padding: 2px 8px;
  z-index: 100;

  &::before {
    content: "";
    position: absolute;
    left: -8px;
    top: 0;
    bottom: 0;
    width: 8px;
    background-color: rgba(234, 234, 234, 1);
    filter: blur(6px);
    z-index: -1;
  }
}

.add-locale-button {
  @include base-font;
  @include font-normal;
  font-weight: 500;
  padding: 8px 16px;
  border: none;
  background-color: rgba(234, 234, 234, 1);
  border-radius: 2px;
  cursor: pointer;
  margin-bottom: 8px;
}

.locale-dropdown {
  position: absolute;
  top: calc(100% - 112px);
  left: 64px;
  max-height: 170px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  text-align: left;
  min-width: 145px;
}
.locale-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
}
.locale-item:hover {
  background: #f5f5f5;
}

.buttons {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 72px;
}

.submit-button {
  @include base-font;
  @include font-small;
  font-weight: 500;
  background: transparent;
  outline: none;
  border: none;
  cursor: pointer;
}
</style>
